import { ColumnDef } from "@tanstack/react-table";
import { ImageIcon } from "assets/icons";
import { t } from "i18next";
import moment from "moment";
import { useMemo } from "react";
import { Button, ItemsTable } from "ui";

import {
  ENRICHMENT_LIST_STATUS_COLORS,
  ENRICHMENT_LIST_STATUS_LABELS
} from "../../constants";
import { EnrichmentListItem } from "../../recommendation.types";
import { Badge } from "../Badge";

import styles from "./EnrichmentTable.module.scss";

interface EnrichmentTableProps {
  items: EnrichmentListItem[];
  isLoading?: boolean;
  enrichingId: string | null;
  onEnrich: (item: EnrichmentListItem) => void;
  onRowClick: (item: EnrichmentListItem) => void;
}

const EnrichmentTable = ({
  items,
  isLoading,
  enrichingId,
  onEnrich,
  onRowClick
}: EnrichmentTableProps) => {
  const columns: Array<ColumnDef<EnrichmentListItem, string>> = useMemo(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: t("Product"),
        size: 44,
        minSize: 44,
        enableSorting: false,
        cell: ({ row }) => {
          const item = items[row.index];

          return (
            <div className={styles.product}>
              <span className={styles.product__icon}>
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt="" loading="lazy" />
                ) : (
                  <ImageIcon width={16} height={16} />
                )}
              </span>
              <span className={styles.name}>
                {item.name ?? t("Unnamed product")}
              </span>
            </div>
          );
        }
      },
      {
        id: "shortId",
        accessorKey: "shortId",
        header: t("Short ID"),
        size: 16,
        minSize: 16,
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.shortId}>
            {items[row.index].shortId ?? "—"}
          </span>
        )
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("Status"),
        size: 14,
        minSize: 14,
        enableSorting: false,
        cell: ({ row }) => {
          const { status, edited } = items[row.index];

          return (
            <div className={styles.status}>
              <Badge
                label={ENRICHMENT_LIST_STATUS_LABELS[status] ?? status}
                color={ENRICHMENT_LIST_STATUS_COLORS[status] ?? "#8E8E93"}
              />
              {edited && <Badge label="Edited" color="#FB3973" />}
            </div>
          );
        }
      },
      {
        id: "enrichedAt",
        accessorKey: "enrichedAt",
        header: t("Enriched"),
        size: 14,
        minSize: 14,
        enableSorting: false,
        cell: ({ row }) => {
          const { enrichedAt } = items[row.index];

          return enrichedAt ? (
            <span className={styles.nowrap}>
              {moment(enrichedAt).format("DD/MM/YYYY")}
            </span>
          ) : (
            <span className={styles.muted}>—</span>
          );
        }
      },
      {
        id: "actions",
        accessorKey: "productId",
        header: "",
        size: 12,
        minSize: 12,
        enableSorting: false,
        cell: ({ row }) => {
          const item = items[row.index];

          return (
            <Button
              variant="default"
              size="sm"
              loading={enrichingId === item.productId}
              onClick={(event) => {
                event.stopPropagation();
                onEnrich(item);
              }}
            >
              {t("Enrich now")}
            </Button>
          );
        }
      }
    ],
    [items, enrichingId]
  );

  return (
    <ItemsTable
      columns={columns}
      data={items}
      isLoading={isLoading}
      skeletonHeight={60}
      emptyListMessage={t("No products in this view")}
      rowsClassName={styles.tallRow}
      onRowClick={onRowClick}
    />
  );
};

export default EnrichmentTable;
